import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import PublicShell, { GrammarStrip } from "@/layouts/PublicShell";
import SEO from "@/components/SEO";
import StateBadge from "@/components/StateBadge";
import { CITY_STATS, ENDPOINTS } from "@/lib/networkData";
import {
  ArrowUpRight,
  ArrowRight,
  Radar,
  Zap,
  MapPin,
  Sparkles,
  ShieldCheck,
  Eye,
  Leaf,
  Cloud,
  Timer,
  Play,
  Store as StoreIcon,
  Wrench,
  Package,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/* Animated Network — SVG city grid, endpoints subtly pulse & connect */
/* ------------------------------------------------------------------ */
function NetworkVisualization({ height = 480 }) {
  const svgRef = useRef(null);
  // Precompute connection lines between clustered endpoints
  const links = React.useMemo(() => {
    const out = [];
    for (let i = 0; i < ENDPOINTS.length; i += 1) {
      const a = ENDPOINTS[i];
      for (let j = i + 1; j < ENDPOINTS.length; j += 1) {
        const b = ENDPOINTS[j];
        if (a.city === b.city) {
          out.push([a, b]);
        }
      }
    }
    return out.slice(0, 80);
  }, []);
  return (
    <svg
      ref={svgRef}
      viewBox="0 0 1000 500"
      preserveAspectRatio="xMidYMid slice"
      style={{ height: `${height}px`, width: "100%" }}
      data-testid="hero-network-viz"
    >
      <defs>
        <radialGradient id="nodeGlow" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#E8D6A3" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#E8D6A3" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* Editorial thin grid */}
      <g stroke="rgba(255,255,255,0.05)" strokeWidth="1">
        {Array.from({ length: 20 }).map((_, i) => (
          <line key={`v${i}`} x1={i * 50} y1={0} x2={i * 50} y2={500} />
        ))}
        {Array.from({ length: 10 }).map((_, i) => (
          <line key={`h${i}`} x1={0} y1={i * 50} x2={1000} y2={i * 50} />
        ))}
      </g>
      {/* Connections */}
      <g stroke="rgba(232,214,163,0.18)" strokeWidth="0.6">
        {links.map(([a, b], i) => (
          <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />
        ))}
      </g>
      {/* Endpoints */}
      {ENDPOINTS.map((e, i) => (
        <g key={e.id}>
          <circle cx={e.x} cy={e.y} r="14" fill="url(#nodeGlow)" opacity="0.6">
            <animate
              attributeName="r"
              from="10"
              to="18"
              dur={`${2 + (i % 5) * 0.4}s`}
              repeatCount="indefinite"
              begin={`${(i % 7) * 0.3}s`}
            />
            <animate
              attributeName="opacity"
              from="0.7"
              to="0"
              dur={`${2 + (i % 5) * 0.4}s`}
              repeatCount="indefinite"
              begin={`${(i % 7) * 0.3}s`}
            />
          </circle>
          <circle
            cx={e.x}
            cy={e.y}
            r="2"
            fill={e.availability === "booked" ? "#00E5FF" : "#E8D6A3"}
          />
        </g>
      ))}
      {/* City labels */}
      {CITY_STATS.map((c) => (
        <g key={c.city}>
          <text
            x={c.x + 10}
            y={c.y + 4}
            fill="rgba(255,255,255,0.55)"
            fontFamily="'JetBrains Mono', ui-monospace, monospace"
            fontSize="9"
            letterSpacing="1"
          >
            {c.city.toUpperCase()} · {c.endpoints}
          </text>
        </g>
      ))}
    </svg>
  );
}

/* ---------- HERO ---------- */
function Hero() {
  return (
    <section className="relative overflow-hidden pt-28 md:pt-32 pb-16 border-b border-white/10">
      <div className="absolute inset-0 opacity-40 pointer-events-none">
        <NetworkVisualization height={640} />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0908]/60 to-[#0A0908]" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="inline-flex items-center gap-3">
          <StateBadge state="BETA" />
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/50">
            Programmable physical media network
          </span>
        </div>
        <h1 className="mt-8 font-display font-black tracking-[-0.045em] leading-[0.88] text-[52px] sm:text-[80px] md:text-[112px] lg:text-[140px]">
          Advertising for the
          <br />
          <span className="text-white/30">Physical World.</span>
        </h1>
        <p className="mt-10 max-w-2xl text-lg md:text-xl text-white/60 leading-relaxed">
          Plan, create, buy and measure advertising across screens, places
          and physical environments — with context, transparency and proof
          at every endpoint.
        </p>
        <div className="mt-10 flex flex-wrap gap-3" data-testid="landing-hero-ctas">
          <Link
            to="/register"
            data-testid="hero-cta-launch"
            className="group flex items-center gap-2 px-5 py-3 bg-white text-black font-semibold hover:bg-white/90 transition"
          >
            Launch a campaign
            <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" />
          </Link>
          <Link
            to="/media-owners"
            data-testid="hero-cta-connect"
            className="group flex items-center gap-2 px-5 py-3 border border-white/20 text-white hover:border-white transition font-semibold"
          >
            Connect a screen
            <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" />
          </Link>
          <Link
            to="/network"
            data-testid="hero-cta-explore"
            className="group flex items-center gap-2 px-5 py-3 text-white/60 hover:text-white transition font-semibold"
          >
            Explore the network →
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ---------- The Physical World Is Becoming Programmable (circular loop) ---------- */
const LOOP_STEPS = [
  ["01", "Physical Surface", "A screen, wall, kiosk, projection."],
  ["02", "Mirrorloops Node", "Software bridge on-device."],
  ["03", "Mirrorloops Network", "Global routing + inventory."],
  ["04", "Campaign Logic", "Rules, budgets, permissions."],
  ["05", "Context", "Weather, time, place, events."],
  ["06", "Creative", "The right asset for the moment."],
  ["07", "Proof of Play", "Verified delivery, per screen."],
  ["08", "Measurement", "Observed · Modelled · Inferred."],
  ["09", "Learning", "Loops back into optimisation."],
];

function LoopSection() {
  return (
    <section className="border-b border-white/10 bg-[#0d0c0b]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-14">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/40 mb-3">
              The Mirror Loop
            </div>
            <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl md:text-6xl max-w-3xl">
              The physical world is becoming programmable.
            </h2>
          </div>
          <p className="max-w-md text-white/60">
            Advertising observes reality → enters reality → receives feedback →
            learns → changes → returns. This is the literal Mirrorloop.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {LOOP_STEPS.map(([n, title, desc]) => (
            <div key={n} className="bg-[#0A0908] p-8 min-h-[180px]">
              <div className="flex items-center gap-3 mb-4">
                <span className="font-mono text-[#E8D6A3] text-sm">{n}</span>
                <span className="h-px flex-1 bg-white/10" />
              </div>
              <div className="font-display font-extrabold text-2xl tracking-tight">
                {title}
              </div>
              <div className="text-white/50 text-sm mt-2 leading-relaxed">{desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Buy Moments, Not Just Placements ---------- */
function MomentsSection() {
  return (
    <section className="border-b border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-2 gap-14 items-center">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#E8D6A3] mb-4 flex items-center gap-2">
            <Timer size={12} /> Moments · not just placements
          </div>
          <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl md:text-6xl">
            Every place is context.
            <br />
            Every moment is programmable.
          </h2>
          <p className="mt-6 text-white/60 text-lg leading-relaxed max-w-lg">
            Buy attention when the weather, the time, the crowd, or the mood
            actually match your creative — not the other way around.
          </p>
          <Link
            to="/programmable"
            data-testid="moments-cta"
            className="mt-8 inline-flex items-center gap-2 px-5 py-3 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF] hover:text-black transition font-semibold"
          >
            See advertising as executable logic <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="space-y-3 font-mono text-sm">
          {[
            ["Cloud size={14} /", "WHEN weather = rain SHOW umbrella creative"],
            ["Timer size={14} /", "WHEN time = 07:00–10:00 SHOW breakfast promo"],
            ["MapPin size={14} /", "WHEN venue ~ train_station SHOW travel offer"],
            ["Eye size={14} /", "WHEN inventory = out_of_stock PAUSE campaign"],
          ].map(([, rule]) => (
            <div
              key={rule}
              className="p-4 border border-white/10 bg-black flex items-center gap-3 text-white/80"
            >
              <span className="text-[#E8D6A3] text-xs">▸</span>
              <span>{rule}</span>
            </div>
          ))}
          <div className="text-[10px] text-white/40 mt-3 tracking-[0.22em] uppercase">
            Rule builder preview · programmable /
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Three routes for three audiences ---------- */
const AUDIENCES = [
  {
    key: "artists",
    title: "For Artists",
    desc: "Musicians, filmmakers, brands, creators. Reach audiences where they actually gather — cafés, gyms, transit, malls.",
    cta: "Launch a campaign",
    to: "/register",
    accent: "#00E5FF",
    tag: "artists",
  },
  {
    key: "businesses",
    title: "For Businesses",
    desc: "Retailers, agencies, brands of every size. Programmable campaigns that respond to weather, time, inventory, event.",
    cta: "Launch a campaign",
    to: "/register",
    accent: "#E8D6A3",
    tag: "businesses",
  },
  {
    key: "people",
    title: "For People",
    desc: "Local causes, community groups, individuals. Advertising you can start with a phone and a purpose — not a media plan.",
    cta: "Start a message",
    to: "/register",
    accent: "#CCFF00",
    tag: "people",
  },
  {
    key: "venues",
    title: "For Venues",
    desc: "Turn your café, mall, or airport into a media network — while protecting the atmosphere of your space.",
    cta: "Connect your venue",
    to: "/venues",
    accent: "#D4B878",
    tag: "venues",
  },
  {
    key: "mediaowners",
    title: "For Media Owners",
    desc: "Make every screen more valuable. Inventory management, programmatic demand, proof of play.",
    cta: "Connect existing screens",
    to: "/media-owners",
    accent: "#a78bfa",
    tag: "media-owners",
  },
];

function AudiencesSection() {
  return (
    <section className="border-b border-white/10 bg-[#0A0908]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="text-[11px] uppercase tracking-[0.22em] text-white/40 mb-4">
          One network · five routes in
        </div>
        <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl md:text-6xl max-w-4xl">
          Advertising for artists, businesses, and people.
          <br />
          <span className="text-white/30">Inventory for venues and media owners.</span>
        </h2>
        <div className="mt-14 grid md:grid-cols-3 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
          {AUDIENCES.map((a, i) => (
            <Link
              key={a.key}
              to={a.to}
              data-testid={`landing-audience-${a.tag}`}
              className="group relative block bg-[#0A0908] hover:bg-white/[0.03] p-8 min-h-[340px] transition"
            >
              <div className="flex items-center justify-between mb-6">
                <span className="font-mono text-[10px]" style={{ color: a.accent }}>
                  0{i + 1} / {a.tag.toUpperCase()}
                </span>
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: a.accent }}
                />
              </div>
              <h3 className="font-display font-extrabold text-2xl md:text-[26px] tracking-tight leading-none mb-4">
                {a.title}
              </h3>
              <p className="text-white/60 leading-relaxed text-sm">{a.desc}</p>
              <div
                className="absolute left-8 bottom-8 inline-flex items-center gap-2 text-xs font-semibold transition group-hover:gap-3"
                style={{ color: a.accent }}
              >
                {a.cta} <ArrowUpRight size={12} />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Mirrorloops Intelligence ---------- */
function IntelligenceSection() {
  return (
    <section className="border-b border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-2 gap-14 items-center">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <StateBadge state="BETA" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-white/40">
              Mirrorloops Intelligence
            </span>
          </div>
          <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl md:text-6xl">
            AI that assists.
            <br />
            <span className="text-white/30">Not a black box.</span>
          </h2>
          <p className="mt-6 text-white/60 text-lg leading-relaxed max-w-lg">
            Intelligence should explain <em>why</em>. Every suggestion inside
            Mirrorloops surfaces its reasoning — accept, modify, or ignore.
          </p>
          <ul className="mt-8 space-y-2 text-sm text-white/70">
            {[
              "AI Media Planning",
              "Creative Generation",
              "Campaign Forecasting",
              "Context Recommendations",
              "Anomaly Detection",
              "Opportunity Detection",
            ].map((f) => (
              <li key={f} className="flex items-center gap-2">
                <span className="text-[#E8D6A3]">·</span>
                {f}
              </li>
            ))}
          </ul>
        </div>
        <div className="border border-white/10 bg-white/[0.02] p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-3">
            Intelligence · Suggested action
          </div>
          <div className="text-lg font-semibold leading-snug">
            "Rain is forecast across{" "}
            <span className="text-[#00E5FF]">34%</span> of your selected
            locations tomorrow. Your rain creative historically performs
            better in these conditions. Shift{" "}
            <span className="text-[#00E5FF]">18%</span> more delivery?"
          </div>
          <div className="mt-6 flex gap-2">
            <button className="px-4 py-2 bg-[#00E5FF] text-black font-semibold text-sm">
              Accept
            </button>
            <button className="px-4 py-2 border border-white/15 text-white/80 text-sm">
              Modify
            </button>
            <button className="px-4 py-2 text-white/40 text-sm">Ignore</button>
          </div>
          <div className="mt-6 text-[11px] text-white/40">
            Reasoning: Historical rain-creative CTR = 3.4× baseline · Forecast
            confidence 82% · Cost neutral.
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Measurement You Can Explain ---------- */
function MeasurementSection() {
  return (
    <section className="border-b border-white/10 bg-[#0d0c0b]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid md:grid-cols-[1fr_1.4fr] gap-14 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/40 mb-4">
              Measurement · Explainable
            </div>
            <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl md:text-6xl">
              Measurement
              <br />
              you can explain.
            </h2>
            <p className="mt-6 text-white/60 max-w-md">
              Never make uncertain measurement look certain. We separate what
              we observed, what we modelled, and what we inferred.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10">
            {[
              ["OBSERVED", "Directly recorded proof of play, per screen, per second.", "#00E5FF"],
              ["MODELLED", "Statistically estimated reach & frequency, with confidence.", "#E8D6A3"],
              ["INFERRED", "Derived from analysis — store visits, search lift, sales.", "#CCFF00"],
            ].map(([k, d, c]) => (
              <div key={k} className="bg-[#0A0908] p-6 min-h-[220px]">
                <div
                  className="font-mono text-xs mb-4"
                  style={{ color: c }}
                >
                  {k}
                </div>
                <div className="text-white/70 text-sm leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Context without Surveillance ---------- */
function ContextSection() {
  return (
    <section className="border-b border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-2 gap-14 items-center">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#CCFF00] mb-4 flex items-center gap-2">
            <ShieldCheck size={12} /> Context · Not Surveillance
          </div>
          <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl md:text-6xl">
            Understand the environment.
            <br />
            <span className="text-white/30">Never the individual.</span>
          </h2>
          <p className="mt-6 text-white/60 text-lg leading-relaxed max-w-lg">
            Privacy is architectural, not a footer disclaimer. We favor
            contextual signals, aggregated movement, consented first-party
            data — and short retention. Full stop.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[
            ["Contextual", true],
            ["Aggregated", true],
            ["Consented", true],
            ["Explainable", true],
            ["Face recognition", false],
            ["Individual tracking", false],
            ["Biometric profiling", false],
            ["Public-space IDs", false],
          ].map(([label, on]) => (
            <div
              key={label}
              className={`p-4 border ${
                on
                  ? "border-emerald-500/40 bg-emerald-500/[0.03] text-emerald-300"
                  : "border-white/10 bg-white/[0.02] text-white/40 line-through"
              }`}
            >
              <div className="text-xs font-bold">{label}</div>
              <div className="text-[10px] uppercase tracking-[0.22em] mt-1 opacity-70">
                {on ? "explicitly favored" : "not designed for"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- The Physical Internet + Labs Studios ---------- */
function PhysicalInternetSection() {
  return (
    <section className="border-b border-white/10 bg-black">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid md:grid-cols-2 gap-14">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <StateBadge state="LABS" />
              <StateBadge state="ROADMAP" />
            </div>
            <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl md:text-6xl">
              A physical internet
              <br />
              for media.
            </h2>
            <p className="mt-6 text-white/60 max-w-md">
              Every screen — an endpoint. Every place — context. Every moment
              — programmable. The Mirrorloops Physical Media Protocol proposes
              a common address, permission, and verification layer for
              physical inventory.
            </p>
            <div className="mt-8 space-y-2">
              <Link
                to="/node"
                data-testid="physicalinternet-cta-node"
                className="block p-4 border border-white/10 hover:border-white/30 transition font-mono text-sm"
              >
                → /node — connect a physical display
              </Link>
              <Link
                to="/os"
                data-testid="physicalinternet-cta-os"
                className="block p-4 border border-white/10 hover:border-white/30 transition font-mono text-sm"
              >
                → /os — operating layer for connected media
              </Link>
              <Link
                to="/labs/mpmp"
                data-testid="physicalinternet-cta-mpmp"
                className="block p-4 border border-white/10 hover:border-white/30 transition font-mono text-sm"
              >
                → /labs/mpmp — the physical media protocol
              </Link>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-4">
              Sample endpoint handle
            </div>
            <div className="p-6 border border-[#D4B878]/20 bg-white/[0.02] font-mono text-sm break-all">
              <span className="text-[#E8D6A3]">mirrorloops://</span>
              india<span className="text-white/40">/</span>mumbai
              <span className="text-white/40">/</span>bandra
              <span className="text-white/40">/</span>venue-014
              <span className="text-white/40">/</span>screen-03
            </div>
            <div className="mt-8">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-3">
                Labs × Studios · The corporate loop
              </div>
              <div className="p-6 border border-white/10 bg-white/[0.02]">
                <div className="font-mono text-xs text-white/60 leading-loose">
                  OBSERVE → UNDERSTAND → CREATE → DEPLOY → MEASURE → LEARN → OBSERVE
                </div>
                <div className="mt-4 text-sm text-white/60">
                  <span className="text-white font-bold">Labs</span> understands
                  and builds the system.{" "}
                  <span className="text-white font-bold">Studios</span> creates
                  what travels through it.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Revolution in Advertising (final CTA) ---------- */
function RevolutionSection() {
  return (
    <section className="border-b border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40 text-center">
        <div className="text-[11px] uppercase tracking-[0.22em] text-white/40 mb-6">
          One more thing
        </div>
        <h2 className="font-display font-black tracking-tighter leading-[0.9] text-5xl md:text-7xl lg:text-8xl">
          A revolution
          <br />
          in advertising.
        </h2>
        <p className="mt-8 text-white/60 max-w-xl mx-auto text-lg">
          Not a slogan. A slow, thoughtful rewrite of how physical
          communication works — one node, one endpoint, one honest
          measurement at a time.
        </p>
        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <Link
            to="/register"
            data-testid="landing-final-cta-launch"
            className="px-6 py-4 bg-white text-black font-bold hover:bg-white/90 transition"
          >
            Launch a campaign
          </Link>
          <Link
            to="/media-owners"
            data-testid="landing-final-cta-connect"
            className="px-6 py-4 border border-white/20 text-white hover:border-white transition"
          >
            Connect a screen
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ---------- ROOT ---------- */
export default function Landing() {
  return (
    <PublicShell>
      <SEO
        title="Advertising for the Physical World"
        description="Mirrorloops is a programmable physical-media network with three products: DOOH advertising, hardware store (displays, kiosks, sensors, mounts), and Mac-powered cyber cafes & coworking. Observed · Modelled · Inferred — playback is not reach."
        path="/"
      />
      <Hero />
      <GrammarStrip />
      <LoopSection />
      <MomentsSection />
      <AudiencesSection />
      <IntelligenceSection />
      <StoreSection />
      <MeasurementSection />
      <ContextSection />
      <PhysicalInternetSection />
      <RevolutionSection />
    </PublicShell>
  );
}

/* ---------- Store · The physical internet begins here ---------- */
function StoreSection() {
  return (
    <section
      data-testid="landing-store-section"
      className="border-b border-white/10 bg-gradient-to-b from-[#0d0c0b] to-[#0A0908]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid md:grid-cols-2 gap-14 items-start">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4B878] mb-4 flex items-center gap-2">
              <StoreIcon size={12} /> Store · Build the Physical Internet
            </div>
            <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl md:text-6xl">
              The store where the
              <br />
              <span className="text-[#D4B878]">physical internet begins.</span>
            </h2>
            <p className="mt-6 text-white/60 text-lg leading-relaxed max-w-lg">
              4,128 curated products across 20 departments — displays, signage,
              retail electronics, kiosks, sensors, mounts. Every SKU is tagged
              for Mirrorloops compatibility so the moment it hits a wall, it can
              start earning attention.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              <Link
                to="/store"
                data-testid="landing-store-cta-browse"
                className="inline-flex items-center gap-2 px-5 py-3 border border-[#D4B878] bg-[#D4B878] text-black hover:bg-[#E8D6A3] font-semibold transition"
              >
                Browse the Store <ArrowUpRight size={14} />
              </Link>
              <Link
                to="/store/build"
                data-testid="landing-store-cta-bom"
                className="inline-flex items-center gap-2 px-5 py-3 border border-[#CCFF00]/60 text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black transition font-semibold"
              >
                <Wrench size={14} /> Build my store →
              </Link>
              <Link
                to="/store/bundles"
                data-testid="landing-store-cta-bundles"
                className="inline-flex items-center gap-2 px-5 py-3 border border-white/20 text-white/80 hover:bg-white hover:text-black transition font-semibold"
              >
                <Package size={14} /> Solution kits
              </Link>
            </div>
          </div>
          {/* Right column — 3 stat/feature cards */}
          <div className="grid gap-3">
            {[
              {
                key: "discover",
                icon: StoreIcon,
                head: "Discover 4,128 products",
                body: "One catalogue, every commerce-facing surface. Filter by size, environment, tier, industry, country.",
                accent: "#E8D6A3",
              },
              {
                key: "build",
                icon: Wrench,
                head: "Build with BOM Builder",
                body: "Answer 5 questions — get a right-sized Bill of Materials for storefront, interior, commerce and infrastructure.",
                accent: "#CCFF00",
              },
              {
                key: "deploy",
                icon: Package,
                head: "Deploy with Mirrorloops",
                body: "Every screen you install becomes a programmable placement the moment you plug it in.",
                accent: "#00E5FF",
              },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.key}
                  data-testid={`landing-store-card-${card.key}`}
                  className="border rounded-lg p-5 bg-white/[0.02] hover:bg-white/[0.04] transition-all"
                  style={{ borderColor: `${card.accent}25` }}
                >
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${card.accent}18`, color: card.accent }}
                  >
                    <Icon size={20} />
                  </div>
                  <div className="text-white font-semibold text-lg mb-1">
                    {card.head}
                  </div>
                  <div className="text-white/60 text-sm leading-relaxed">
                    {card.body}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
